import React, { useContext } from 'react';
import {
    Button as MuiButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Alert as MuiAlert,
    CircularProgress,
    Box,
    Typography,
    MenuItem,
    Grid,
    Stack,
    Divider,
    useTheme,
    InputAdornment
} from '@mui/material';
import { 
    Close as CloseIcon, 
    Warning as WarningIcon, 
    CheckCircle, 
    Info, 
    Error as ErrorIcon 
} from '@mui/icons-material';
import { Eye, EyeOff } from 'lucide-react';
import { ThemeContext } from '../context/ThemeContext.jsx';
import { DynamicIcon } from './icons.jsx';
import { motion } from 'framer-motion';

// --- Loading Spinner ---
export const LoadingSpinner = ({ size, className = '' }) => {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" p={2} className={className}>
            <CircularProgress size={size || 40} />
        </Box>
    );
};

// --- Page Header ---
export const PageHeader = ({ title, description, children }) => (
    <Box mb={3} display="flex" flexDirection="column" gap={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                {title}
            </Typography>
            <Box>{children}</Box>
        </Box>
        {description && (
            <Typography variant="body2" color="text.secondary">
                {description}
            </Typography>
        )}
    </Box>
);

// --- Button ---
// Mapping variants: primary -> contained, secondary -> outlined/text, danger -> contained color error
export const Button = ({ children, onClick, variant = 'primary', className = '', type = 'button', disabled = false, size = 'medium', withShadow = false, ...props }) => {
    let muiVariant = 'contained';
    let color = 'primary';

    if (variant === 'secondary') {
        muiVariant = 'outlined';
        color = 'inherit'; // or primary with outlined
    } else if (variant === 'ghost') {
        muiVariant = 'text';
        color = 'inherit';
    } else if (variant === 'danger') {
        muiVariant = 'contained';
        color = 'error';
    }

    return (
        <MuiButton
            type={type}
            onClick={onClick}
            variant={muiVariant}
            color={color}
            disabled={disabled}
            size={size === 'icon' ? 'small' : size}
            className={className}
            sx={{
                textTransform: 'none',
                minWidth: size === 'icon' ? 'auto' : undefined,
                ...(withShadow ? {
                    boxShadow: 2,
                    bgcolor: muiVariant === 'outlined' ? 'background.paper' : undefined,
                    '&:hover': {
                        boxShadow: 3,
                        bgcolor: muiVariant === 'outlined' ? 'background.paper' : undefined
                    }
                } : {})
            }}
            {...props}
        >
            {children}
        </MuiButton>
    );
};

// --- Modal ---
export const Modal = ({ isOpen, onClose, title, children, zIndex }) => {
    return (
        <Dialog
            open={!!isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ zIndex: zIndex || 1300 }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {title}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {children}
            </DialogContent>
        </Dialog>
    );
};

// --- Confirm Modal ---
export const ConfirmModal = ({
    isOpen,
    onClose = null,
    onConfirm,
    title,
    children = null,
    zIndex = 1300,
    message = null,
    onCancel = null,
    isDestructive = true,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
}) => {
    const handleCancel = onCancel || onClose || (() => {});
    return (
        <Dialog
            open={!!isOpen}
            onClose={handleCancel}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
            sx={{ zIndex: zIndex || 1300 }}
        >
            <DialogTitle id="confirm-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color={isDestructive ? 'error' : 'warning'} />
                {title}
            </DialogTitle>
            <DialogContent>
                <Typography id="confirm-dialog-description">
                    {children || message}
                </Typography>
            </DialogContent>
            <DialogActions>
                <MuiButton onClick={handleCancel} color="inherit">{cancelText}</MuiButton>
                <MuiButton onClick={onConfirm} color={isDestructive ? 'error' : 'primary'} variant="contained" autoFocus>
                    {confirmText}
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
};

// --- Alert ---
export const Alert = ({ message, type = 'info' }) => {
    return (
        <MuiAlert severity={type} sx={{ mb: 2 }}>
            {message}
        </MuiAlert>
    );
};


// --- Form Input ---
export const FormInput = ({ label, id, readOnly, InputProps, ...props }) => (
    <Box mb={2}>
        <TextField
            fullWidth
            label={label}
            id={id}
            variant="outlined"
            size="small"
            disabled={readOnly}
            InputProps={{
                readOnly: readOnly,
                ...InputProps
            }}
            {...props}
        />
    </Box>
);

// --- Password Input (Reusable Eye Icon) ---
export const PasswordInput = React.forwardRef(({ label, id, onFocus, onBlur, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    // [PasswordInput Debug] 
    // useEffect(() => { console.debug(`[PasswordInput Debug] ${label} - Visible: ${showPassword}`); }, [showPassword]);

    return (
        <Box mb={2}>
            <TextField
                fullWidth
                label={label}
                id={id}
                inputRef={ref}
                type={showPassword ? "text" : "password"}
                variant="outlined"
                size="small"
                onFocus={(e) => {
                    setIsFocused(true);
                    if (onFocus) onFocus(e);
                }}
                onBlur={(e) => {
                    setIsFocused(false);
                    if (onBlur) onBlur(e);
                }}
                InputProps={{
                    ...props.InputProps,
                    endAdornment: (
                        <InputAdornment 
                            position="end" 
                        >
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.preventDefault()} // Prevents blur when clicking icon
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                {...props}
            />
        </Box>
    );
});

// --- Form Select ---
// Children are expected to be <option> tags, but MUI Select uses MenuItem.
// If the children are literal options, we might need adjustments.
// Assuming callers pass <option> elements, we might need to intercept children or assume usages will be updated?
// Actually, `FilterPanel.jsx` passes `<option>`. MUI TextField with `select` prop renders a Select.
// However, it expects `MenuItem` as children, NOT `option`.
// We need to support `option` children for backward compatibility OR wrap them?
// Actually, native select is safer for quick migration if we want to avoid refactoring all callsites to generic MenuItems.
// So let's use `Select` native mode or just `TextField` with `SelectProps={{ native: true }}`.
export const FormSelect = ({ label, id, children, value, onChange, ...props }) => (
    <Box mb={2}>
        <TextField
            select
            fullWidth
            label={label}
            id={id}
            value={value}
            onChange={onChange}
            variant="outlined"
            size="small"
            SelectProps={{
                native: true,
            }}
            InputLabelProps={{ shrink: true }}
            {...props}
        >
            {children}
        </TextField>
    </Box>
);

// --- Form Textarea ---
export const FormTextarea = ({ label, id, ...props }) => (
    <Box mb={2}>
        <TextField
            fullWidth
            label={label}
            id={id}
            multiline
            rows={4}
            variant="outlined"
            {...props} // includes value, onChange etc
        />
    </Box>
);

// --- Stat Card ---
export const StatCard = ({ title, value, iconName, colorClass }) => {
    // Extract color from colorClass (e.g., 'bg-blue-500')?
    // This might be tricky if it's a complex tailwind class.
    // For now, render the icon in a Box with the class.
    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: 2,
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                height: '100%'
            }}
        >
            <Box
                className={colorClass}
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white' // Assuming colorClass provides background
                }}
            >
                <DynamicIcon name={iconName} className="w-6 h-6" />
            </Box>
            <Box>
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    {title}
                </Typography>
                <Typography variant="h5" color="text.primary" fontWeight="bold">
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

// --- Standard Page Layout (Pro SaaS) ---
export const StandardPageLayout = ({ title, description, headerActions, filterControls, children }) => {
    const theme = useTheme();
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 120px)',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <Box sx={{
                flexShrink: 0,
                mb: 2,
                pb: 1,
                px: 0,
                pt: 0
            }}>
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12} md={4}>
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <Typography variant="h4" fontWeight="700" color="text.primary">
                                {title}
                            </Typography>
                        </motion.div>
                        {description && (
                            <motion.div
                                key={description}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 }}
                            >
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                                    {description}
                                </Typography>
                            </motion.div>
                        )}
                    </Grid>
                    <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0.5 }, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
                        {filterControls}
                        {headerActions}
                    </Grid>
                </Grid>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{
                flexGrow: 1,
                overflowY: 'auto',
                px: 0,
                pt: 1,
                pb: 3,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: '4px' },
                '&:hover::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[400] },
            }}>
                {children}
            </Box>
        </Box>
    );
};